<template>
  <div class="select-avanced flex flex-col relative">
    <label
      v-if="label"
      :for="id"
      class="block text-sm font-semibold dark:text-white"
      :class="{ 'text-red-500': isError }"
    >
      {{ label }} <span class="text-red-500">{{ required ? '*' : '' }}</span>
    </label>
    <div class="relative">
      <div
        v-if="loading"
        class="flex min-h-9.5 w-full items-center rounded border border-gray-300 bg-white py-2 px-3 dark:border-gray-700 dark:bg-slate-900"
        aria-hidden="true"
      >
        <div
          class="h-2.5 w-2/5 animate-pulse rounded-full bg-gray-200 dark:bg-neutral-700"
        ></div>
        <svg
          class="ms-auto size-4 shrink-0 animate-spin text-gray-400 dark:text-neutral-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      </div>
      <div v-show="!loading" class="relative min-h-9.5">
        <select
          :id="id"
          v-bind="$attrs"
          v-model="model"
          :multiple="multiple"
          class="hidden w-full"
        >
          <template v-if="groupOptions">
            <template
              v-for="(opciones, grupo) in optionsGrouped"
              :key="grupo"
            >
              <option
                :value="`__grupo_${grupo}`"
                disabled
              >
                {{ grupo }}
              </option>
              <option
                v-for="item of opciones"
                :key="mapOptions ? mapOptions(item as object).optionValue : optionValue && (item as any)[optionValue]"
                :value="mapOptions ? mapOptions(item as object).optionValue : optionValue && (item as any)[optionValue]"
                :data-hs-select-option="mapOptions ? buildHsOption(mapOptions(item as object)) : undefined"
              >
                {{
                  mapOptions
                    ? mapOptions(item as object).optionText
                    : optionText && (item as object)[optionText]
                }}
              </option>
            </template>
          </template>
          <template v-else>
            <option
              v-for="item of optionsDefault"
              :key="mapOptions ? mapOptions(item as object).optionValue : optionValue && (item as any)[optionValue]"
              :value="mapOptions ? mapOptions(item as object).optionValue : optionValue && (item as any)[optionValue]"
              :data-hs-select-option="mapOptions ? buildHsOption(mapOptions(item as object)) : undefined"
            >
              {{
                mapOptions
                  ? mapOptions(item as object).optionText
                  : optionText && (item as object)[optionText]
              }}
            </option>
          </template>
        </select>
        <button
          v-if="clear && hasValue"
          type="button"
          class="absolute inset-y-0 right-8 z-10 inline-flex items-center text-gray-500 dark:text-neutral-400"
          aria-label="Limpiar selección"
          @click.stop="limpiarSeleccion"
        >
          <XMarkIcon class="size-5 rounded-md bg-blue-50 hover:bg-blue-100" />
        </button>
      </div>
    </div>
    <ErrorTag
      :is-error="isError"
      :error-message="errorMessage"
    />
  </div>
</template>

<script setup lang="ts" generic="T">
import { ref, onMounted, nextTick, watch, computed, onUnmounted } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import type { ModelValue } from '@/modules/Core/usings/constantes'
import * as services from '@/services'
import type { ServicesType, ISelectOption } from '@/modules/Core/interfaces'
import { getIsError } from '@/modules/Core/usings/generales'
import type { ErrorObject } from '@vuelidate/core'
import ErrorTag from './ErrorTag.vue'

const model = defineModel<ModelValue>()
const props = defineProps<{
  id: string
  label?: string
  options?: unknown[]
  optionValue?: string
  optionText?: string
  mapOptions?: (item: object) => ISelectOption
  urlSearch?: keyof ServicesType
  action?: string
  multiple?: boolean
  params?: string | Record<string, string>
  error?: string | Array<ErrorObject> | null | undefined
  required?: boolean
  searchable?: boolean
  clear?: boolean
  groupOptions?: (item: object) => string | null | undefined
  optionTemplate?: string
  disabled?: boolean
  placeholder?: string
}>()

const errorMessage = computed(()=> props.error)

const isError = computed(() => {
  const isArray = Array.isArray(props.error)
  return getIsError(props, isArray)
})
const {
  id,
  multiple = false,
  urlSearch,
  action,
  params,
  required = false,
  searchable = true,
  clear = false
} = props
const optionsDefault = ref(props.options)
const loading = ref(!!(urlSearch && action))
const optionsGrouped = computed(() => {
  const grupos: Record<string, unknown[]> = {}

  for (const opcion of optionsDefault.value ?? []) {
    const grupo = props.groupOptions?.(opcion as object) || 'Sin grupo'
    grupos[grupo] ??= []
    grupos[grupo].push(opcion)
  }

  return grupos
})
const hasValue = computed(() => {
  if (Array.isArray(model.value)) return model.value.length > 0
  return model.value !== null && model.value !== undefined && model.value !== ''
})

const limpiarSeleccion = () => {
  model.value = multiple ? [] : null
}

const buildHsOption = (mapped: ISelectOption): string | undefined => {
  const obj: Record<string, string> = {}
  if (mapped.optionIcon) obj.icon = mapped.optionIcon
  if (mapped.optionSubText) obj.description = mapped.optionSubText
  return Object.keys(obj).length ? JSON.stringify(obj) : undefined
}

const getSelectConfig = () => {
  if (!multiple) {
    return {
      placeholder: props.placeholder ?? 'Selecciona un item',
      toggleTag: '<button type="button" aria-expanded="false"></button>',
      toggleClasses:
        'hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative flex text-nowrap w-full cursor-pointer bg-white border border-gray-600 rounded text-start text-sm py-2 px-3 focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600',
      dropdownClasses:
        'mt-2 z-50 w-full max-h-72 bg-white border border-gray-600 rounded-lg overflow-hidden overflow-y-auto dark:bg-slate-900 dark:border-gray-700',
      optionClasses:
        'py-2 px-4 w-full text-sm text-gray-800 cursor-pointer hover:bg-gray-100 rounded-lg focus:outline-none focus:bg-gray-100 dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-gray-200 dark:focus:bg-slate-800',
      optionTemplate: props.optionTemplate ??
        '<div class="flex items-center"><div class="w-8 h-8 me-2" data-icon></div><div><div class="text-sm font-semibold text-gray-800 dark:text-neutral-200" data-title></div><div class="text-xs text-gray-500 dark:text-neutral-500" data-description></div></div><div class="ms-auto"><span class="hidden hs-selected:block"><svg class="shrink-0 w-4 h-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"/></svg></span></div></div>',
      ...(searchable && {
        hasSearch: true,
        searchPlaceholder: 'Buscar...',
        searchClasses: 'py-2 px-3 block w-full text-sm border border-gray-600 rounded focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400 dark:focus:ring-gray-600',
        searchWrapperTemplate: '<div class="p-2 pb-0"></div>'
      })
    }
  }

  return {
    placeholder: props.placeholder ?? 'Selecciona opciones',
    mode: 'tags',
    wrapperClasses:
      'relative ps-0.5 pe-9 min-h-[46px] flex items-center flex-wrap text-nowrap w-full border border-gray-600 rounded text-start text-sm bg-white focus-within:border-blue-500 focus-within:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400',
    tagsItemTemplate:
      '<div class="flex flex-nowrap items-center relative z-10 bg-white border border-gray-400 rounded-full p-1 m-1 max-w-full min-w-0 dark:bg-neutral-900 dark:border-neutral-700"><div class="truncate text-gray-800 dark:text-neutral-200 px-2 max-w-[260px]" data-title></div><div class="inline-flex shrink-0 justify-center items-center w-5 h-5 ms-1 rounded-full text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 text-sm dark:bg-neutral-700/50 dark:hover:bg-neutral-700 dark:text-neutral-400 cursor-pointer" data-remove><svg class="shrink-0 w-3 h-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></div></div>',
    tagsInputClasses:
      'py-2 px-2 order-1 shadow-none outline-none border-0 bg-transparent text-sm text-gray-800 focus:ring-0 dark:bg-slate-900 dark:text-gray-400',
    dropdownClasses:
      'mt-2 z-[60] w-full max-h-72 p-1 space-y-0.5 bg-white border border-gray-600 rounded-lg overflow-x-auto overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500 dark:bg-slate-900 dark:border-gray-700',
    optionClasses:
      'py-2 px-4 w-max min-w-full text-sm text-gray-800 cursor-pointer hover:bg-gray-100 rounded-lg focus:outline-none focus:bg-gray-100 dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-gray-200 dark:focus:bg-slate-800',
    optionTemplate:
      '<div class="flex justify-between items-center gap-3 w-full"><span class="whitespace-nowrap" data-title></span><span class="hidden hs-selected:block"><svg class="shrink-0 w-3.5 h-3.5 text-blue-600" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>',
    extraMarkup:
      '<div class="absolute top-1/2 end-3 -translate-y-1/2"><svg class="shrink-0 w-3.5 h-3.5 text-gray-500 dark:text-neutral-500" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/></svg></div>',
    ...(searchable && {
      hasSearch: true,
      searchPlaceholder: 'Buscar opciones...',
      searchClasses: 'block w-full text-sm border border-gray-600 rounded focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400 dark:focus:ring-gray-600 py-2 px-3',
      searchWrapperTemplate: '<div class="bg-white p-2 sticky top-0 dark:bg-slate-900"></div>'
    })
  }
}

const isUpdating = ref(false)
let widthObserver: ResizeObserver | null = null

// Preline calcula el ancho del input de tags al construir el select. Si nace dentro
// de un modal o tab oculto el wrapper mide 0 y el placeholder queda cortado, así que
// recalculamos en cuanto el wrapper tiene un ancho real.
const observeTagsInputWidth = (hselect: { wrapper?: HTMLElement; calculateInputWidth?: () => void }) => {
  widthObserver?.disconnect()
  widthObserver = null

  if (!multiple || !hselect?.wrapper) return

  let lastWidth = hselect.wrapper.offsetWidth
  widthObserver = new ResizeObserver(() => {
    const width = hselect.wrapper?.offsetWidth ?? 0
    if (!width || width === lastWidth) return
    lastWidth = width
    hselect.calculateInputWidth?.()
  })
  widthObserver.observe(hselect.wrapper)
}

const handleClickOutside = (event: MouseEvent) => {
  if (!multiple) return

  const select = document.getElementById(id) as HTMLSelectElement
  if (!select) return

  const selectInstance = window.HSSelect.getInstance(select)
  if (!selectInstance) return

  // Verificar si el dropdown está abierto
  const dropdown = selectInstance.dropdown
  if (!dropdown || !dropdown.classList.contains('block')) return

  // Verificar si el click fue fuera del componente select y su dropdown
  const selectElement = select.closest('.hs-select')
  const isClickInsideSelect = selectElement && selectElement.contains(event.target as Node)
  const isClickInsideDropdown = dropdown.contains(event.target as Node)

  if (!isClickInsideSelect && !isClickInsideDropdown) {
    selectInstance.close()
  }
}

// Reinicializar cuando el tab se vuelva visible
const reinitializeIfVisible = () => {
  const select = document.getElementById(id) as HTMLSelectElement
  if (!select) return

  const tabContainer = select.closest('[role="tabpanel"]')
  if (tabContainer) {
    const isVisible = !tabContainer.classList.contains('hidden')
    if (isVisible) {
      setTimeout(() => {
        load()
      }, 150)
    }
  }
}

const load = async () => {
  try {
    if (urlSearch && action) {
      loading.value = true
      const { data } = await (services[urlSearch] as object)[action](params)
      optionsDefault.value = data.body
    }
    await nextTick()

    const select = document.getElementById(id) as HTMLSelectElement
    if (!select) return

    widthObserver?.disconnect()
    widthObserver = null

    // Limpiar instancia anterior si existe
    const existingInstance = window.HSSelect.getInstance(select)
    if (existingInstance) {
      // Neutralizar callbacks asíncronos pendientes antes de destruir
      ;(['adjustInputWidth', 'reassignTagsInputPlaceholder', 'buildTagsInputHelper'] as const).forEach(fn => {
        if (typeof (existingInstance as Record<string, unknown>)[fn] === 'function') {
          ;(existingInstance as Record<string, unknown>)[fn] = () => {}
        }
      })
      if (existingInstance.dropdown) {
        existingInstance.dropdown.remove()
      }
      existingInstance.destroy()
      select.removeAttribute('data-hs-select')
    }

    await nextTick()

    if (multiple && !Array.isArray(model.value)) {
      model.value = model.value ? [String(model.value)] : []
    }

    loading.value = false
    await nextTick()

    select.setAttribute('data-hs-select', JSON.stringify(getSelectConfig()))
    const hselect = new window.HSSelect(select)
    observeTagsInputWidth(hselect)

    hselect.on('change', (val: string | string[]) => {
      if (isUpdating.value) return
      try {
        isUpdating.value = true
        if (multiple) {
          model.value = Array.isArray(val) ? val : [val]
        } else {
          model.value = val
        }
      } finally {
        setTimeout(() => {
          isUpdating.value = false
        }, 100)
      }
    })

    // Agregar listener para clicks fuera del dropdown si es múltiple
    if (multiple) {
      document.addEventListener('click', handleClickOutside)
    }
  } catch (error) {
    console.error('Error al inicializar HSSelect:', error)
  } finally {
    loading.value = false
  }
}

watch(
  () => model.value,
  async newVal => {
    if (!id || isUpdating.value) return

    try {
      isUpdating.value = true
      await nextTick()
      const select = document.getElementById(id) as HTMLSelectElement
      if (!select) return

      widthObserver?.disconnect()
      widthObserver = null

      const selectInstance = window.HSSelect.getInstance(select)
      if (selectInstance) {
        // Neutralizar callbacks asíncronos pendientes antes de destruir
        if (typeof selectInstance.adjustInputWidth === 'function') {
          selectInstance.adjustInputWidth = () => {}
        }
        if (selectInstance.dropdown) {
          selectInstance.dropdown.remove()
        }
        selectInstance.destroy()
        select.removeAttribute('data-hs-select')
        await nextTick()
      }

      if (multiple) {
        const values = Array.isArray(newVal) ? newVal : newVal ? [newVal] : []
        values.forEach(val => {
          const option = select.querySelector(`option[value="${val}"]`)
          if (option) {
            (option as HTMLOptionElement).selected = true
          }
        })
      } else {
        select.value = String(newVal || '')
      }

      select.setAttribute('data-hs-select', JSON.stringify(getSelectConfig()))
      observeTagsInputWidth(new window.HSSelect(select))

      // Re-agregar listener para clicks fuera del dropdown si es múltiple
      if (multiple) {
        document.addEventListener('click', handleClickOutside)
      }
    } catch (error) {
      console.error('Error al actualizar HSSelect:', error)
    } finally {
      setTimeout(() => {
        isUpdating.value = false
      }, 100)
    }
  },
  { deep: true }
)

let timeoutId: number | null = null
let pendingOptions: unknown[] | undefined

// Reconstruye HSSelect de forma diferida agrupando los cambios seguidos
// (opciones y configuración) en una sola reinicialización.
const scheduleReload = (newOptions?: unknown[]) => {
  if (newOptions !== undefined) pendingOptions = newOptions
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
  timeoutId = setTimeout(async () => {
    if (pendingOptions !== undefined) {
      optionsDefault.value = pendingOptions
      pendingOptions = undefined
    }
    await load()
  }, 150) as unknown as number
}

if (props.options) {
  watch(() => props.options, newOptions => scheduleReload(newOptions), { deep: true })
}

// El placeholder viaja dentro de la config de HSSelect, así que hay que reconstruirlo.
// El atributo `disabled` no lo necesita: Preline lo observa y alterna la clase solo.
watch(() => props.placeholder, () => scheduleReload())

onMounted(() => {
  const cleanup = () => {
    document.removeEventListener('click', handleClickOutside)
    const select = document.getElementById(id) as HTMLSelectElement
    if (select) {
      const selectInstance = window.HSSelect.getInstance(select)
      if (selectInstance?.dropdown) {
        selectInstance.dropdown.remove()
      }
    }
  }

  window.addEventListener('beforeunload', cleanup)

  // Escuchar eventos de tabs para reinicializar
  const handleTabShow = () => {
    setTimeout(() => {
      reinitializeIfVisible()
    }, 200)
  }

  document.addEventListener('change.hs.tab', handleTabShow)
  document.addEventListener('show.hs.tab', handleTabShow)

  nextTick().then(() => {
    load()
  })

  onUnmounted(() => {
    document.removeEventListener('change.hs.tab', handleTabShow)
    document.removeEventListener('show.hs.tab', handleTabShow)
    window.removeEventListener('beforeunload', cleanup)
    cleanup()
  })
})

onUnmounted(() => {
  try {
    // Remover listener de clicks fuera
    document.removeEventListener('click', handleClickOutside)
    widthObserver?.disconnect()
    widthObserver = null

    const select = document.getElementById(id) as HTMLSelectElement
    if (select) {
      const selectInstance = window.HSSelect.getInstance(select)
      if (selectInstance) {
        if (selectInstance.dropdown) {
          selectInstance.dropdown.remove()
        }
        try {
          selectInstance.destroy()
        } catch (destroyError) {
          console.warn('Error al destruir HSSelect:', destroyError)
        }
      }
    }
  } catch (error) {
    console.warn('Error en la limpieza del componente:', error)
  }
})
</script>

<style scoped>
/*
  Preline (HSSelect) construye los items del dropdown en runtime, por eso el
  separador de grupo se estiliza por su data-value y no con clases en el <option>.
*/
.select-avanced :deep([data-value^='__grupo_']) {
  --grupo-bg: #f9fafb;
  --grupo-text: #6b7280;
  --grupo-border: #e5e7eb;

  margin-top: 0.25rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0;
  background-color: var(--grupo-bg);
  color: var(--grupo-text);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: default;
  pointer-events: none;
}

.select-avanced :deep([data-value^='__grupo_']:is(.dark, .dark *)) {
  --grupo-bg: #262626;
  --grupo-text: #a3a3a3;
  --grupo-border: #404040;
}

/* Línea divisoria solo entre grupos, nunca encima del primero */
.select-avanced :deep([data-value]:not([data-value^='__grupo_']) + [data-value^='__grupo_']) {
  margin-top: 0.5rem;
  border-top: 1px solid var(--grupo-border);
}

/* El optionTemplate trae su propio tamaño/color de texto */
.select-avanced :deep([data-value^='__grupo_'] [data-title]) {
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
}
</style>
